import OlMap from 'ol/Map';
import OpusLayer from 'lib/classes/opuslayer';
import ColumnType from 'lib/enums/columntype';
import parseHTML from 'html-react-parser';

import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { getOpusLayerByCode } from 'lib/utilities/map';
import { updateURLParameter, parseURLs } from 'lib/utilities/misc';
import { fetchRequest } from 'lib/utilities/network';
import { assetURL } from 'lib/utilities/asset';
import { getLocaleString } from 'lib/localization/vendor';
import { assumeColumnKindByName } from 'lib/utilities/datacolumns';

import SearchResultPopup from "views/popups/SearchResult";

import 'styles/css/SearchResultPopup.scss';

const parse = (value:string):any => {
	if(value === undefined) value = '';
	value = String(value);
	value = value.split('\n').join('<br />');
	value = value.split('\r').join('<br />');
	return parseHTML(value);
}

export default function SearchResult({title, olMap, result, expandedView}:{title:string, olMap:OlMap, result:any, expandedView?:boolean}) {
	const resultRef = useRef<HTMLDivElement>();
	const [tooLong, setTooLong] = useState(false);
	
	const opusLayer:OpusLayer = getOpusLayerByCode(result.layerId, olMap);
	const urlsParsed:any[] = parseURLs(result['urls']);
	const [ocDocItems, setOcDocItems] = useState<Map<string, any>>(new Map<string, any>());
	
	// Initialize
	useEffect(() => {
		loadOCDocumentURLs();
	}, [result]);
	
	useLayoutEffect(() => {
		if(!resultRef.current)
			return;
			
		const el = resultRef.current;
		
		setTooLong(
			el.scrollWidth > el.clientWidth ||
			el.scrollHeight > el.clientHeight || 
			el.scrollHeight > 2000 
		);
	}, [result]);
	
	const loadOCDocumentURLs = () => {
		if (!urlsParsed) return;
	
		// batch with URL + label context
		const batch: Promise<{ body: string; url: string; label: string }>[] = [];
	
		urlsParsed.forEach(urlObj => {
			const urlFinal: string = urlObj.url.trim();
	
			if (urlFinal.includes('/readdoc') && urlFinal.includes('oc2.uk')) {
				const urlRequest: string = `https://m.opusmap.co.uk/opus4ac/opusconsult.php?url=${encodeURIComponent(urlFinal)}`;
	
				batch.push(
					fetchRequest(urlRequest, {})
						.then(res => res.text())
						.then(body => ({
							body,
							url: urlFinal,
							label: urlObj.label
						}))
				);
			}
		});
	
		Promise.all(batch).then(results => {
			const ocDocItemsNew: Map<string, any> = new Map();
	
			results.forEach(({ body, url, label }) => {
				const docId: number = Number(url.split('/de/')[1]);
	
				const docuData = body.split(`id="d${docId}"></a>`)[1]?.split('</div>')[0] || '';
				const summary: string = docuData
					.split('<p>')[1]
					?.split('</p>')[0]
					?.replace(/<[^>]*>/g, '')
					.trim() || '';
	
				const canComment = docuData.includes('user-make-comment');
				const canViewComments = docuData.includes('user-view-comment');
	
				const onClickDocument = () => window.open(url, '_blank');
				const onClickComment = () => window.open(`https://demo.oc2.uk/repwizard/${docId}`, '_blank');
				const onClickViewComment = () => window.open(`https://demo.oc2.uk/document/reps/${docId}`, '_blank');
	
				const drawDocButton = () => (
					<div className="oc-doc-result__user-button clickable" onClick={onClickDocument}>
						<img src={assetURL('assets/images/oc_viewdoc.png')} />
					</div>
				);
				const drawCommentButton = () => (
					<div className="oc-doc-result__user-button clickable" onClick={onClickComment}>
						<img src={assetURL('assets/images/oc_comment.png')} />
					</div>
				);
				const drawViewCommentsButton = () => (
					<div className="oc-doc-result__user-button clickable" onClick={onClickViewComment}>
						<img src={assetURL('assets/images/oc_search.png')} />
					</div>
				);
	
				const docDraw = (
					<div className="oc-doc-result" key={docId}>
						<div className="oc-doc-result__user-buttons">
							{drawDocButton()}
							{canComment && drawCommentButton()}
							{canViewComments && drawViewCommentsButton()}
						</div>
	
						<div className="oc-doc-result__external">
							<p className="ext-url">
								<a href={url} target="_blank">
									{label} {/* ✅ Show label instead of URL */}
									<img src={assetURL('assets/images/external.png')} className="ql-result__icon" />
								</a>
							</p>
							<p>{summary}</p>
						</div>
					</div>
				);
	
				ocDocItemsNew.set(url, docDraw);
			});
	
			setOcDocItems(ocDocItemsNew);
		});
	};
	const showExpandedResult = () => {
		SearchResultPopup.set("title", title);
		SearchResultPopup.set("search-result", result);
		SearchResultPopup.set("map", olMap);
		
		_ui.showPopup(SearchResultPopup);
	}
	
	const drawResults = () => {
		/* Result rows */
		const resultRows:any[] = [];
		
		/* Image URL */
		let url:string = result.image;
		
		url = updateURLParameter(url, 'width', '120');
		url = updateURLParameter(url, 'height', '60');
		
		const drawResultRow = (title:string, value:any) => {
			return (
				<div className='ql-result__data-set' key={resultRows.length}>
					{!!title && (
						<div className='ql-result__sub'>
							{title}
						</div>
					)}
					<div className='ql-result__data-content'>
						{value}
					</div>
				</div>
			)
		}
		const drawNoDataLabel = () => {
			return (
				<div className='gray-label'>
					{getLocaleString('nodata')}
				</div>
			)
		}
		/* Draw URLs Method */
		const drawURLs = (urls:any[]) => {
			const rows:any[] = [];
			
			if(!urls)
				return rows;
			
			urls.forEach(url => {
				if(!url || !url.label || !url.url)
					return;
				
				const label:string = url.label;
				const urlFinal:string = url.url.trim();
				
				// For documents only
				const ocDocDraw:any = ocDocItems.get(urlFinal);
				
				if(urlFinal.includes('google.com/maps/') && urlFinal.includes('map_action=pano')) {
					
					const showStreetView = () => {
						const urlParams:URLSearchParams = new URLSearchParams(urlFinal);
						const viewpoint:string = urlParams.get('viewpoint');
						const lat:number = Number(viewpoint.split(',')[0]);
						const lng:number = Number(viewpoint.split(',')[1]);
						
						_ui.openGoogleMapViewAt(lng, lat, urlFinal);
					}
					
					rows.push(
						<p key={urls.indexOf(url)}>
							<button onClick={() => showStreetView()}>
								{label} 
							</button> 
						</p>
					);
				}else
				if(urlFinal.includes('google.com/maps/') && urlFinal.includes('basemap=satellite')) {
					
					const showSatelliteView = () => {
						const urlParams:URLSearchParams = new URLSearchParams(urlFinal);
						const viewpoint:string = urlParams.get('center');
						const lat:number = Number(viewpoint.split(',')[0]);
						const lng:number = Number(viewpoint.split(',')[1]);
						
						_ui.openGoogleMapViewAt(lng, lat, urlFinal, {mapType: 'satellite', mapMode: 'view'});
					}
					
					rows.push(
						<p key={urls.indexOf(url)}>
							<button onClick={() => showSatelliteView()}>
								{label} 
							</button> 
						</p>
					);
				}else
				if(urlFinal.includes('/readdoc') && urlFinal.includes('oc2.uk') && ocDocDraw) {
					
					rows.push(ocDocDraw);
					
				}else{
					rows.push(
						<p className="ext-url" key={urls.indexOf(url)}>
							<a href={urlFinal} target='_blank'>
								{label} 
								<img src={assetURL('assets/images/external.png')} className='ql-result__icon' />
							</a> 
						</p>
					);
				}
			});
			
			return rows;
		}
		
		// Try to parse overlay file JSON
		let overlayFileJsonObj:any = null;
		try {
			if(result.overlayFileJson)
				overlayFileJsonObj = JSON.parse(result.overlayFileJson);
		}catch(e:any) {};
		
		// Template data object created by Opus4ACReact when customising how layers present their overlay data. It is *NOT* the overlay data itself.
		let templateDataObj:any;
		
		try {
			if(typeof opusLayer.json === 'string')
				templateDataObj = JSON.parse(opusLayer.json);
		}catch(e) {
			console.error('Parsing template layer json error', e);
		}
		
		const allFields:any = {
			description: result.description,
			policies: result.policies,
			urls: result.urls,
			...result.customFields
		};
		const columnNames:string[] = [];
		const templateCols:any[] = Array.isArray(templateDataObj?.columns) ? templateDataObj?.columns : [];
		
		for(let colName in allFields) {
			columnNames.push(colName);
		}
		
		const columnNamesOrdered = columnNames.sort((a:string, b:string) => {
			const idxA:number = templateCols.findIndex((c:any) => c.name == a);
			const idxB:number = templateCols.findIndex((c:any) => c.name == b);
			
			if(idxA === idxB)
				return 0;
			if(idxA > idxB)
				return 1;
			if(idxA < idxB)
				return -1;
		});
		
		columnNamesOrdered.forEach((columnName:string) => {
			const templateObj:any = templateCols.find((c:any) => c.name == columnName);
			const value:any = allFields[columnName];
			let visualName:string = templateObj?.displayName ?? columnName;
			
			if(templateObj?.hidden === true)
				return;
			
			if(templateObj?.localeMask === 'en-GB' && _config.lang.id !== 'en-GB')
				return;
				
			if(templateObj?.localeMask === 'cy-GB' && _config.lang.id !== 'cy-GB')
				return;
			
			let toRender:any;
			
			if(!value && value !== '' && value !== 0)
				return;
				
			const columnTypes:any = overlayFileJsonObj?.columnTypes;
			const customColType:ColumnType = columnTypes?.[columnName];
			const columnType:ColumnType = customColType !== undefined ? customColType : assumeColumnKindByName(columnName);
			
			switch(columnType) {
				case ColumnType.URLs:
					toRender = drawURLs(parseURLs(value));
				break;
				default:
					toRender = parse(value);
				break;
			}
				
			if(visualName == 'title')
				visualName = getLocaleString('title');
				
			if(visualName == 'description')
				visualName = getLocaleString('description');
				
			if(visualName == 'policies')
				visualName = getLocaleString('policies');
				
			if(visualName == 'urls')
				visualName = getLocaleString('urls');
				
			if(templateObj?.noheader === true)
				visualName = '';
			
			resultRows.push(
				drawResultRow( visualName, toRender )
			);
		});
		
		return resultRows.length > 0 ? resultRows : drawNoDataLabel();
	}
	const drawTooLongButton = () => (
		<div className='ql-result__data-too-long bubble'>
			<p><b>⚠️ {getLocaleString('value-too-long')}</b></p>
			<button type="submit" onClick={() => showExpandedResult()}>
				<img className='icon' style={{width: 15, opacity: 0.4, marginRight: 10}} src={assetURL('assets/images/expand.png')} />  
				{getLocaleString('show-expanded-result')}
			</button>
		</div>
	)
	
	return (
		<div ref={resultRef} className='ql-result'>
			<div className='ql-result__data-id'>
				OpusMap ID: {result.id}
			</div>
			{tooLong && !expandedView && drawTooLongButton()}
			{drawResults()}
			{tooLong && !expandedView && drawTooLongButton()}
		</div>
	)
}